// ============================================================================
// ReceiptCard — one-row confirmation for a tool action
// status ring · entity title · kind+date mono · deep link. Never taller
// than one row.
// ============================================================================

import { Box, Typography } from '@mui/material'
import { typography, spacing } from '@/theme/muiTheme'
import type { RenderPayload } from '@/types/chatRenders'
import { cardChromeSx, monoMetaSx, StatusRing, CardActionLink } from './CardShell'
import { entityRoute, statusColor, formatWeekdayDate } from './cardUtils'

const STATUS_GLYPH: Record<string, string> = {
  created: '✓',
  updated: '✓',
  removed: '−',
  failed: '!',
}

/** Contextual action label per the frames ('View on calendar →', 'Open view →'). */
function actionLabel(kind: string): string {
  switch (kind) {
    case 'workout':
    case 'schedule_entry':
      return 'View on calendar →'
    case 'widget':
    case 'view':
      return 'Open view →'
    default:
      return 'Open →'
  }
}

export default function ReceiptCard({ render }: { render: RenderPayload }) {
  const { entity, status } = render
  const route = entityRoute(entity)
  const date = formatWeekdayDate(entity.date)
  // Status is carried by the ring; the kind row is just kind + date context.
  const meta = [entity.kind.replace(/_/g, ' '), date].filter(Boolean).join(' · ')

  return (
    <Box
      sx={{
        ...cardChromeSx,
        my: spacing.xs,
        px: spacing.md,
        py: spacing.sm,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        minWidth: 0,
      }}
    >
      <StatusRing color={statusColor(status)} glyph={STATUS_GLYPH[status] ?? '✓'} size={18} />
      {/* Title stacked over the kind row (receipt anatomy in the frames). */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          noWrap
          sx={{
            fontSize: '14px',
            fontWeight: typography.fontWeightMedium,
            minWidth: 0,
            ...(status === 'removed' ? { color: 'text.secondary' } : {}),
          }}
        >
          {entity.title || entity.kind.replace(/_/g, ' ')}
        </Typography>
        <Typography noWrap sx={{ ...monoMetaSx, fontSize: '9px', mt: '2px' }}>
          {meta}
        </Typography>
      </Box>
      {route && status !== 'failed' && (
        <CardActionLink action={{ label: actionLabel(entity.kind), to: route }} />
      )}
    </Box>
  )
}
